import type { AxiosInstance } from "axios";
import type {
  AirtableSLATableData,
  AllUsersResponse,
  Auth0Role,
  Auth0User,
  Auth0UserCreate,
  Auth0UserUpdate,
  Banner,
  Company,
  CompanyResponse,
  ProcessDoc,
  DueDiligence,
  DueDiligenceFile,
  SharedDocument,
  Training,
  TrainingType,
  Update,
  UpdateAvailableMonth,
  User,
  ProjectSummary,
  CompanyConfig,
  HubspotCompanyLinks,
  CompanyManagement,
  RoadmapData,
  KeyContacts,
  UpdateCategory,
  TransferProvider,
  TransferProviderSummary,
  OnrampProjectData,
} from "./types";

export const api = {
  init: (client: AxiosInstance) => ({
    user: {
      getUserInfo: async () => {
        const res = await client.get<User>(`getUser`);
        return res.data;
      },
      changePassword: async () => {
        const res = await client.post(`changePassword`);
        return res.data;
      },
      updateUserName: async (name: User["name"]) => {
        const res = await client.patch<User>(`updateUserName`, { name: name });
        return res.data;
      },
    },
    utility: {
      buildSearch: async () => {
        const res = await client.get(`buildDocsSearch`);
        return res.data;
      },
    },
    keyContacts: {
      get: async () => {
        const res = await client.get<KeyContacts>(`getKeyContacts`);
        return res.data;
      },
    },
    banner: {
      get: async () => {
        const res = await client.get<Banner>(`getBanner`);
        return res.data;
      },
    },
    company: {
      get: async () => {
        const res = await client.get<CompanyResponse>(`getCompany`);
        return res.data;
      },
      getConfig: async () => {
        const res = await client.get<CompanyConfig>(`getCompanyConfig`);
        return res.data;
      },
      getManagementStructure: async () => {
        const res = await client.get<CompanyManagement>(`getCompanyManagement`);
        return res.data;
      },
      getLinks: async () => {
        const res = await client.get<HubspotCompanyLinks>("getCompanyLinks");
        return res.data;
      },
    },
    documentation: {
      getAll: async () => {
        const res = await client.get<ProcessDoc[]>("getDocs");
        return res.data;
      },
    },
    dueDiligence: {
      getAll: async () => {
        const res = await client.get<DueDiligence[]>("getDueDiligence");
        return res.data;
      },
      getFile: async () => {
        const res = await client.get<DueDiligenceFile>("getDueDiligenceFile");
        return res.data;
      },
    },
    sharedDocuments: {
      getAll: async () => {
        const res = await client.get<SharedDocument[]>("getSharedDocuments");
        return res.data;
      },
    },
    update: {
      getAll: async (
        companyStatus: string,
        maxRecords?: number,
        category?: UpdateCategory,
        month?: UpdateAvailableMonth,
        statusFilter?: "unpublished" | "published",
      ) => {
        const res = await client.get<Update[]>("getUpdates", {
          params: {
            companyStatus: companyStatus,
            maxRecords: maxRecords,
            category: category,
            month: month?.dateMonth,
            year: month?.dateYear,
            statusFilter: statusFilter,
          },
        });
        return res.data;
      },
      getAvailableMonths: async (companyStatus: string) => {
        const res = await client.get<UpdateAvailableMonth[]>(
          "getUpdatesAvailableMonths",
          { params: { companyStatus: companyStatus } },
        );
        return res.data;
      },
      get: async (companyStatus: string, updateSlug: string) => {
        const res = await client.get<Update>("getUpdate", {
          params: { companyStatus: companyStatus, slug: updateSlug },
        });
        return res.data;
      },
    },
    roadmap: {
      getAll: async () => {
        const res = await client.get<RoadmapData>("getRoadmap");
        return res.data;
      },
    },
    projectSummary: {
      getAll: async () => {
        const res = await client.get<ProjectSummary>("getProjectSummaryDocs");
        return res.data;
      },
    },
    airtableSLAs: {
      getAll: async () => {
        const res = await client.get<AirtableSLATableData>("getAirtableSLAs");
        return res.data;
      },
    },
    trainingVideos: {
      getAll: async (type: TrainingType) => {
        const res = await client.get<Training[]>("getTrainingVideos", {
          params: {
            type,
          },
        });
        return res.data;
      },
    },
    onramp: {
      getProjects: async () => {
        const res = await client.get<OnrampProjectData[]>("getOnrampProjects");
        return res.data;
      },
    },
    transferProviders: {
      getAll: async () => {
        const res = await client.get<TransferProviderSummary[]>(
          "getTransferProviders",
        );
        return res.data;
      },
      get: async (slug: string) => {
        const res = await client.get<TransferProvider>(
          "getTransferProvider",
          { params: { slug } },
        );
        return res.data;
      },
    },
    admin: {
      user: {
        getAll: async (
          page: number,
          companyFilter?: string,
          search?: string,
        ) => {
          const res = await client.get<AllUsersResponse>(`getUsers`, {
            params: {
              page: page,
              companyId: companyFilter,
              search: search,
            },
          });
          return res.data;
        },
        get: async (userId: string) => {
          const res = await client.get<Auth0User>(`getUser?id=${userId}`);
          return res.data;
        },
        update: async (data: Auth0UserUpdate) => {
          const res = await client.patch<Auth0User>(`updateUser`, data);
          return res.data;
        },
        create: async (data: Auth0UserCreate) => {
          const res = await client.post<Auth0User>(`createUser`, data);
          return res.data;
        },
        delete: async (userId: string) => {
          const res = await client.delete<Auth0User>(`deleteUser?id=${userId}`);
          return res.data;
        },
        downloadAllByCompany: async (
          companyId: string,
          excludeSecclUsers = true,
        ) => {
          const res = await client.get(`downloadUserList`, {
            params: { companyId, excludeSecclUsers },
            responseType: "blob",
          });
          return res;
        },
      },
      roles: {
        getAll: async () => {
          const res = await client.get<Auth0Role[]>(`getRoles`);
          return res.data;
        },
      },
      company: {
        getAll: async () => {
          const res = await client.get<Company[]>("getCompanies");
          return res.data;
        },
      },
    },
  }),
};

export type ApiType = ReturnType<(typeof api)["init"]>;
